import { useCallback, useEffect, useRef } from 'react';

// Failsafe cadence: poll the media clock rather than trusting a single
// pre-computed delay, and cap the total wait so a stuck clock still advances.
const FAILSAFE_POLL_MS = 250;
const FAILSAFE_HARD_CAP_MS = 10_000;

interface UseAutoAdvanceParams {
  correctSelected: boolean;
  onCorrect?: (value?: any) => void;
  getDurationAndPosition: () => { duration: number; position: number };
  onBeforeAdvance?: () => void;
  resetKey?: string;
  successDelay?: number;
}

/**
 * Owns the single "advance to next exercise" event. The natural media-end
 * (`onMediaFinished`) and the failsafe fallback both funnel through one
 * idempotent `commit`, so the exercise can only advance once — no matter which
 * path wins the race (related to SDB-358).
 */
export const useAutoAdvance = ({
  correctSelected,
  onCorrect,
  getDurationAndPosition,
  onBeforeAdvance,
  resetKey,
  successDelay = 1000,
}: UseAutoAdvanceParams) => {
  const committedRef = useRef<boolean>(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const onCorrectRef = useRef(onCorrect);
  const onBeforeAdvanceRef = useRef(onBeforeAdvance);
  const getDurationAndPositionRef = useRef(getDurationAndPosition);
  const correctSelectedRef = useRef(correctSelected);
  onCorrectRef.current = onCorrect;
  onBeforeAdvanceRef.current = onBeforeAdvance;
  getDurationAndPositionRef.current = getDurationAndPosition;
  correctSelectedRef.current = correctSelected;

  const commit = useCallback(() => {
    if (committedRef.current) {
      return;
    }
    committedRef.current = true;
    clearTimeout(timerRef.current);
    onBeforeAdvanceRef.current?.();
    onCorrectRef.current?.();
  }, []);

  const onMediaFinished = useCallback(() => {
    if (committedRef.current || !correctSelectedRef.current) {
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(commit, successDelay);
  }, [commit, successDelay]);

  // Failsafe: advance even if the media-end event never arrives.
  // We poll the live media clock instead of pre-computing a single delay,
  // because when `correctSelected` flips the media has usually not loaded yet
  // (duration === 0). A one-shot fallback then fires on a stale or zero
  // duration and cuts long sentences short on success — most visibly on web,
  // where duration/position are read from refs that lag real playback.
  useEffect(() => {
    if (!correctSelected || committedRef.current) {
      return;
    }

    const startedAt = Date.now();
    let graceTimer: ReturnType<typeof setTimeout> | undefined;

    const poll = setInterval(() => {
      const { duration, position } = getDurationAndPositionRef.current();

      // Media clock known and reached the end → advance after the grace pause.
      if (duration > 0 && position >= duration) {
        clearInterval(poll);
        graceTimer = setTimeout(commit, successDelay);
        return;
      }

      // Absolute safety net: never hang if the clock never advances at all.
      if (Date.now() - startedAt >= FAILSAFE_HARD_CAP_MS) {
        clearInterval(poll);
        commit();
      }
    }, FAILSAFE_POLL_MS);

    return () => {
      clearInterval(poll);
      clearTimeout(graceTimer);
    };
  }, [correctSelected, commit, successDelay]);

  // Reset the guard when a new exercise loads
  useEffect(() => {
    committedRef.current = false;
    return () => clearTimeout(timerRef.current);
  }, [resetKey]);

  return { commit, onMediaFinished };
};
